import { useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useForms from './hooks/useForms';
import FormsView from './view';
import FormBuilderModal from '@components/_modals/Forms/FormBuilderModal';
import TemplatesModal from '@components/_modals/Forms/TemplatesModal';
import api from '@helpers/api';
import FormsSubmissions from './submissions';

export default function Forms() {
    const { slug, subtab } = useParams();
    const navigate = useNavigate();
    const state = useForms();
    const modalRef = useRef({});
    const templatesModalRef = useRef({});

    const subTabs = useMemo(() => [
        {
            key: 'list',
            label: 'Formulários',
            path: `/${slug}/settings/forms`,
        },
        {
            key: 'submissions',
            label: 'Submissões',
            path: `/${slug}/settings/forms/submissions`,
            component: <FormsSubmissions />
        }
    ], [slug]);

    const activeSubTab = subtab === 'submissions' ? 'submissions' : 'list';
    const activeTabConfig = subTabs.find(t => t.key === activeSubTab);

    function handleCreateFromScratch() {
        modalRef.current.open(state.currentCommunity, () => state.handleLoad());
    }

    function handleUseTemplate() {
        templatesModalRef.current.open((templateData) => {
            modalRef.current.openWithTemplate(state.currentCommunity, templateData, () => state.handleLoad());
        });
    }

    function handleEditForm(form) {
        modalRef.current.edit(state.currentCommunity, form.id, () => state.handleLoad());
    }

    function handleDuplicateForm(form) {
        const { id, community_id, active, created_at, updated_at, ...templateData } = form;

        const cleanedData = {
            ...templateData,
            title: `${templateData.title || ''} (Cópia)`,
            steps: (templateData.steps ?? []).map(({ id: _sid, ...step }) => ({
                ...step,
                questions: (step.questions ?? []).map(({ id: _qid, ...question }) => question),
            })),
        };

        modalRef.current.openWithTemplate(state.currentCommunity, cleanedData, () => state.handleLoad());
    }

    async function handleToggleActive(form, active) {
        try {
            await api.patch(`forms/${form.id}`, { active });
            state.handleLoad();
        } catch (e) {
            console.error(e);
        }
    }

    async function handleDeleteForm(form) {
        try {
            await api.delete(`forms/${form.id}`);
            state.handleLoad();
        } catch (e) {
            console.error(e);
        }
    }

    return (
        <>
            <FormsView
                {...state}
                subTabs={subTabs}
                activeSubTab={activeSubTab}
                activeComponent={activeTabConfig?.component}
                onSubTabChange={(key) => {
                    const target = subTabs.find(t => t.key === key);
                    if (target) navigate(target.path);
                }}
                onCreateFromScratch={handleCreateFromScratch}
                onUseTemplate={handleUseTemplate}
                onEditForm={handleEditForm}
                onDuplicateForm={handleDuplicateForm}
                onToggleActive={handleToggleActive}
                onDeleteForm={handleDeleteForm}
            />
            <FormBuilderModal modalRef={modalRef} communityId={state.currentCommunity?.id} />
            <TemplatesModal modalRef={templatesModalRef} />
        </>
    );
}
